import { acquirePersistentMicrophone } from "./persistentMicrophone";

export const VOICE_CONTROL_STATUS = Object.freeze({
  DISABLED: "disabled",
  AWAITING_PERMISSION: "awaiting_permission",
  REQUESTING_PERMISSION: "requesting_permission",
  ACTIVE: "active",
  LISTENING: "listening",
  PROCESSING: "processing",
  PERMISSION_DENIED: "permission_denied",
  UNAVAILABLE: "unavailable",
  UNSUPPORTED: "unsupported",
  ERROR: "error",
});

const STATUS_COPY = Object.freeze({
  [VOICE_CONTROL_STATUS.DISABLED]: {
    es: "Control de voz desactivado",
    en: "Voice control disabled",
    de: "Sprachsteuerung deaktiviert",
    ru: "Голосовое управление отключено",
  },
  [VOICE_CONTROL_STATUS.AWAITING_PERMISSION]: {
    es: "Control de voz activado. Permiso del microfono pendiente",
    en: "Voice control enabled. Microphone permission pending",
    de: "Sprachsteuerung aktiviert. Mikrofonberechtigung ausstehend",
    ru: "Голосовое управление включено. Ожидается разрешение микрофона",
  },
  [VOICE_CONTROL_STATUS.REQUESTING_PERMISSION]: {
    es: "Solicitando permiso del microfono",
    en: "Requesting microphone permission",
    de: "Mikrofonberechtigung wird angefragt",
    ru: "Запрос разрешения микрофона",
  },
  [VOICE_CONTROL_STATUS.ACTIVE]: {
    es: "Control de voz activo",
    en: "Voice control active",
    de: "Sprachsteuerung aktiv",
    ru: "Голосовое управление активно",
  },
  [VOICE_CONTROL_STATUS.LISTENING]: {
    es: "Escuchando",
    en: "Listening",
    de: "Hoert zu",
    ru: "Слушает",
  },
  [VOICE_CONTROL_STATUS.PROCESSING]: {
    es: "Procesando comando",
    en: "Processing command",
    de: "Befehl wird verarbeitet",
    ru: "Обработка команды",
  },
  [VOICE_CONTROL_STATUS.PERMISSION_DENIED]: {
    es: "Permiso del microfono rechazado",
    en: "Microphone permission denied",
    de: "Mikrofonberechtigung abgelehnt",
    ru: "Разрешение микрофона отклонено",
  },
  [VOICE_CONTROL_STATUS.UNAVAILABLE]: {
    es: "Microfono no disponible",
    en: "Microphone unavailable",
    de: "Mikrofon nicht verfuegbar",
    ru: "Микрофон недоступен",
  },
  [VOICE_CONTROL_STATUS.UNSUPPORTED]: {
    es: "Reconocimiento de voz no compatible",
    en: "Speech recognition not supported",
    de: "Spracherkennung nicht unterstuetzt",
    ru: "Распознавание речи не поддерживается",
  },
  [VOICE_CONTROL_STATUS.ERROR]: {
    es: "Error del control de voz",
    en: "Voice control error",
    de: "Fehler der Sprachsteuerung",
    ru: "Ошибка голосового управления",
  },
});

const LANGUAGE_TO_RECOGNITION_LOCALE = Object.freeze({
  es: "es-ES",
  en: "en-US",
  de: "de-DE",
  ru: "ru-RU",
});

const KEY_DATA = Object.freeze({
  ArrowUp: { key: "ArrowUp", code: "ArrowUp", keyCode: 38 },
  ArrowDown: { key: "ArrowDown", code: "ArrowDown", keyCode: 40 },
  ArrowLeft: { key: "ArrowLeft", code: "ArrowLeft", keyCode: 37 },
  ArrowRight: { key: "ArrowRight", code: "ArrowRight", keyCode: 39 },
  Enter: { key: "Enter", code: "Enter", keyCode: 13 },
  Escape: { key: "Escape", code: "Escape", keyCode: 27 },
  Space: { key: " ", code: "Space", keyCode: 32 },
  KeyQ: { key: "q", code: "KeyQ", keyCode: 81 },
  KeyE: { key: "e", code: "KeyE", keyCode: 69 },
  Equal: { key: "+", code: "Equal", keyCode: 187 },
  Minus: { key: "-", code: "Minus", keyCode: 189 },
});

const COMMANDS = Object.freeze([
  {
    id: "camera_left",
    type: "camera",
    label: "Camara izquierda",
    keyData: { ...KEY_DATA.ArrowLeft, shiftKey: true },
    match: (text) => hasCameraIntent(text) && hasAny(text, RIGHT_WORDS),
  },
  {
    id: "camera_right",
    type: "camera",
    label: "Camara derecha",
    keyData: { ...KEY_DATA.ArrowRight, shiftKey: true },
    match: (text) => hasCameraIntent(text) && hasAny(text, LEFT_WORDS),
  },
  {
    id: "camera_up",
    type: "camera",
    label: "Camara arriba",
    keyData: { ...KEY_DATA.ArrowUp, shiftKey: true },
    match: (text) => hasVerticalCameraCommand(text, UP_WORDS),
  },
  {
    id: "camera_down",
    type: "camera",
    label: "Camara abajo",
    keyData: { ...KEY_DATA.ArrowDown, shiftKey: true },
    match: (text) => hasVerticalCameraCommand(text, DOWN_WORDS),
  },
  {
    id: "rotate_45_right",
    type: "rotation",
    label: "Rotar 45 grados derecha",
    keyData: KEY_DATA.KeyQ,
    match: (text) => hasFortyFiveRightCommand(text),
  },
  {
    id: "rotate_45_left",
    type: "rotation",
    label: "Rotar 45 grados izquierda",
    keyData: KEY_DATA.KeyE,
    match: (text) => hasFortyFiveLeftCommand(text),
  },
  {
    id: "rotate_left",
    type: "rotation",
    label: "Rotar izquierda",
    keyData: KEY_DATA.KeyQ,
    match: (text) => hasHorizontalRotationIntent(text, RIGHT_WORDS),
  },
  {
    id: "rotate_right",
    type: "rotation",
    label: "Rotar derecha",
    keyData: KEY_DATA.KeyE,
    match: (text) => hasHorizontalRotationIntent(text, LEFT_WORDS),
  },
  {
    id: "rotate_180",
    type: "rotation",
    label: "Rotar 180 grados",
    keyData: KEY_DATA.KeyE,
    match: (text) =>
      !hasFullTurnIntent(text) &&
      (hasHalfTurnIntent(text) ||
        (hasRotationIntent(text) && !hasAny(text, LEFT_WORDS) && !hasAny(text, RIGHT_WORDS))),
  },
  {
    id: "rotate_360",
    type: "rotation",
    label: "Rotar 360 grados",
    keyData: KEY_DATA.KeyE,
    match: (text) => hasFullTurnIntent(text),
  },
  {
    id: "zoom_in",
    type: "camera",
    label: "Acercar",
    keyData: KEY_DATA.Equal,
    match: (text) => hasAny(text, ["acercar", "acerca", "acercate", "aumenta", "zoom in", "ampliar", "mas zoom"]),
  },
  {
    id: "zoom_out",
    type: "camera",
    label: "Alejar",
    keyData: KEY_DATA.Minus,
    match: (text) =>
      hasAny(text, ["alejar", "aleja", "alejate", "disminuye", "disminulle", "zoom out", "reducir", "menos zoom"]),
  },
  {
    id: "center_board",
    type: "camera",
    label: "Centrar tablero",
    keyData: null,
    match: (text) => hasCenterBoardIntent(text),
  },
  {
    id: "focus_corner",
    type: "camera",
    label: "Enfocar esquina",
    keyData: null,
    match: (text) => hasCornerFocusIntent(text),
  },
  {
    id: "face_player:1",
    type: "camera",
    label: "Cara uno",
    keyData: null,
    match: (text) => hasPlayerFaceIntent(text, 1),
  },
  {
    id: "face_player:2",
    type: "camera",
    label: "Cara dos",
    keyData: null,
    match: (text) => hasPlayerFaceIntent(text, 2),
  },
  {
    id: "face_player:3",
    type: "camera",
    label: "Cara tres",
    keyData: null,
    match: (text) => hasPlayerFaceIntent(text, 3),
  },
  {
    id: "face_player:4",
    type: "camera",
    label: "Cara cuatro",
    keyData: null,
    match: (text) => hasPlayerFaceIntent(text, 4),
  },
  {
    id: "select",
    type: "action",
    label: "Seleccionar",
    keyData: KEY_DATA.Enter,
    match: (text) =>
      hasAny(text, ["seleccionar", "selecciona", "aceptar", "acepta", "confirmar", "confirma", "enter", "ok"]),
  },
  {
    id: "cancel",
    type: "action",
    label: "Cancelar",
    keyData: KEY_DATA.Escape,
    match: (text) => hasAny(text, ["cancelar", "cancela", "escape", "salir", "atras", "volver"]),
  },
  {
    id: "roll_dice",
    type: "dice",
    label: "Lanzar dados",
    keyData: null,
    match: (text) => hasRollDiceIntent(text),
  },
  {
    id: "roll_or_space",
    type: "action",
    label: "Accion",
    keyData: KEY_DATA.Space,
    match: (text) =>
      hasAny(text, ["espacio", "accion"]),
  },
  {
    id: "move_up",
    type: "movement",
    label: "Arriba",
    keyData: KEY_DATA.ArrowUp,
    match: (text) => hasAny(text, UP_WORDS),
  },
  {
    id: "move_down",
    type: "movement",
    label: "Abajo",
    keyData: KEY_DATA.ArrowDown,
    match: (text) => hasAny(text, DOWN_WORDS),
  },
  {
    id: "move_left",
    type: "movement",
    label: "Izquierda",
    keyData: KEY_DATA.ArrowLeft,
    match: (text) => hasAny(text, LEFT_WORDS),
  },
  {
    id: "move_right",
    type: "movement",
    label: "Derecha",
    keyData: KEY_DATA.ArrowRight,
    match: (text) => hasAny(text, RIGHT_WORDS),
  },
]);

const DUPLICATE_TRANSCRIPT_WINDOW_MS = 1200;
const REPEAT_COMMAND_GUARD_WINDOW_MS = 2600;

const UP_WORDS = ["arriba", "subir", "sube", "adelante", "norte", "up", "forward"];
const DOWN_WORDS = ["abajo", "bajar", "baja", "sur", "down", "back"];
const LEFT_WORDS = ["izquierda", "izquierdo", "left", "oeste"];
const RIGHT_WORDS = ["derecha", "derecho", "right", "este"];
const BOARD_FILE_WORDS = Object.freeze({
  a: "a",
  b: "b",
  be: "b",
  ve: "b",
  c: "c",
  ce: "c",
  d: "d",
  de: "d",
  e: "e",
  f: "f",
  efe: "f",
  g: "g",
  ge: "g",
  h: "h",
  hache: "h",
  i: "i",
  y: "i",
  ye: "i",
  j: "j",
  jota: "j",
  k: "k",
  ka: "k",
  l: "l",
  ele: "l",
  m: "m",
  eme: "m",
});
const BOARD_ROW_WORDS = Object.freeze({
  uno: 1,
  un: 1,
  una: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
  diez: 10,
  once: 11,
  doce: 12,
  trece: 13,
});
const DIE_VALUE_WORDS = Object.freeze({
  uno: 1,
  un: 1,
  una: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
});

const normalizeLanguage = (language) => String(language || "es").slice(0, 2).toLowerCase();

const hasCameraIntent = (text) =>
  hasAny(text, ["camara", "camera", "vista", "view", "mirar"]);

const hasRotationIntent = (text) =>
  hasAny(text, ["rotar", "rota", "girar", "gira", "turn", "rotate"]);

const hasHalfTurnIntent = (text) =>
  hasAny(text, ["media vuelta", "atras"]);

const hasFullTurnIntent = (text) => {
  const normalized = normalizeAngleCommandText(text);
  return normalized === "gira" || normalized === "girar";
};

const normalizeAngleCommandText = (text) =>
  normalizeVoiceText(text)
    .replace(/\bgrados?\b/g, "")
    .replace(/\bdegree?s?\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

const hasFortyFiveRightCommand = (text) => {
  const normalized = normalizeAngleCommandText(text);
  return (
    normalized === "45" ||
    normalized === "gira 45" ||
    normalized === "girar 45" ||
    normalized === "rota 45" ||
    normalized === "rotar 45" ||
    normalized === "cuarenta y cinco" ||
    normalized === "mas"
  );
};

const hasFortyFiveLeftCommand = (text) => {
  const normalized = normalizeAngleCommandText(text);
  return (
    normalized === "menos 45" ||
    normalized === "menos cuarenta y cinco" ||
    normalized === "gira menos 45" ||
    normalized === "girar menos 45" ||
    normalized === "rota menos 45" ||
    normalized === "rotar menos 45" ||
    normalized === "menos"
  );
};

const isBareHorizontalDirectionCommand = (text, directionWords) => {
  const normalized = normalizeVoiceText(text)
    .replace(/^a la\s+/, "")
    .replace(/^hacia la\s+/, "")
    .replace(/^para la\s+/, "")
    .trim();
  return directionWords.some((word) => normalized === normalizeVoiceText(word));
};

const hasHorizontalRotationIntent = (text, directionWords) =>
  hasAny(text, directionWords) &&
  (hasRotationIntent(text) ||
    hasCameraIntent(text) ||
    isBareHorizontalDirectionCommand(text, directionWords) ||
    hasExplicitRepeatIntent(text));

const isBareVerticalDirectionCommand = (text, directionWords) => {
  const normalized = normalizeVoiceText(text)
    .replace(/^hacia\s+/, "")
    .replace(/^para\s+/, "")
    .trim();
  return directionWords.some((word) => normalized === normalizeVoiceText(word));
};

const hasVerticalCameraCommand = (text, directionWords) =>
  (hasCameraIntent(text) && hasAny(text, directionWords)) ||
  isBareVerticalDirectionCommand(text, directionWords) ||
  (hasAny(text, directionWords) && hasExplicitRepeatIntent(text));

const hasCenterBoardIntent = (text) =>
  hasAny(text, ["centro", "medio", "posicion inicial", "posicion de inicio", "vista inicial"]);

const hasCornerFocusIntent = (text) =>
  hasAny(text, ["esquina"]);

const PLAYER_FACE_WORDS = Object.freeze({
  1: ["cara uno", "cara 1", "cara jugador uno", "cara jugador 1", "cara azul"],
  2: ["cara dos", "cara 2", "cara jugador dos", "cara jugador 2", "cara roja", "cara rojo"],
  3: ["cara tres", "cara 3", "cara jugador tres", "cara jugador 3", "cara amarilla", "cara amarillo"],
  4: ["cara cuatro", "cara 4", "cara jugador cuatro", "cara jugador 4", "cara verde"],
});

const hasPlayerFaceIntent = (text, playerNumber) =>
  hasAny(text, PLAYER_FACE_WORDS[playerNumber] || []);

const hasRollDiceIntent = (text) => {
  const normalized = normalizeVoiceText(text);
  return [
    "lanzar",
    "lanza",
    "lanzar dado",
    "lanzar dados",
    "lanza dado",
    "lanza dados",
    "tirar dado",
    "tirar dados",
    "tira dado",
    "tira dados",
    "dados",
    "los dados",
    "roll dice",
  ].includes(normalized);
};

const parseDieChoiceValueToken = (token) => {
  const normalized = normalizeVoiceText(token);
  if (/^[1-6]$/.test(normalized)) return Number(normalized);
  return DIE_VALUE_WORDS[normalized] || null;
};

const buildDieChoiceCommand = (dieValue) => ({
  id: `choose_die:${dieValue}`,
  type: "dice",
  label: `Dado ${dieValue}`,
  keyData: null,
  target: String(dieValue),
});

const parseDieChoiceCommand = (text) => {
  const normalized = normalizeVoiceText(text);
  const exactValue = parseDieChoiceValueToken(normalized);
  if (exactValue) return buildDieChoiceCommand(exactValue);

  if (
    !hasAny(normalized, [
      "dado",
      "numero",
      "número",
      "usa",
      "usar",
      "usa el",
      "usar el",
      "elige",
      "elegir",
      "escoge",
      "escoger",
      "selecciona",
      "seleccionar",
    ])
  ) {
    return null;
  }

  const values = normalized
    .split(" ")
    .map(parseDieChoiceValueToken)
    .filter((value) => value >= 1 && value <= 6);
  const uniqueValues = [...new Set(values)];
  if (uniqueValues.length !== 1) return null;
  return buildDieChoiceCommand(uniqueValues[0]);
};

const hasFocusCellIntent = (text) =>
  hasAny(text, [
    "enfoca",
    "foca",
    "enfocar",
    "focar",
    "enfoque",
    "enfoca casilla",
    "foca casilla",
    "enfocar casilla",
    "centra",
    "centrar",
    "ubica",
    "ubicar",
    "muestra",
    "mostrar",
  ]);

const hasSpecialBlockIntent = (text) =>
  hasAny(text, ["especial", "bloque especial", "pieza especial"]);

const INCORPORATE_WORDS = Object.freeze([
    "incorpora",
    "incoorpora",
    "incoopora",
    "incorporar",
    "incoorporar",
    "incooporar",
    "incorporan",
    "incoorporan",
    "incooporan",
    "incorporen",
    "incoorporen",
    "incooporen",
    "incorpore",
    "incoorpore",
    "incoopore",
    "incorporalo",
    "incoorporalo",
    "incooporalo",
    "incorporando",
    "incoorporando",
    "incooporando",
    "incorporame",
    "incoorporame",
    "incooporame",
    "mete",
    "meter",
    "entra",
    "entrar",
]);

const hasIncorporateIntent = (text) => {
  if (hasAny(text, INCORPORATE_WORDS)) return true;

  const compactText = normalizeVoiceText(text).replace(/\s+/g, "");
  return INCORPORATE_WORDS.some((phrase) =>
    compactText.includes(normalizeVoiceText(phrase).replace(/\s+/g, ""))
  );
};

const hasMoveIntent = (text) =>
  hasAny(text, ["mueve", "mover", "mueva", "muebe", "muevan", "move"]);

const hasMoveOriginIntent = (text) =>
  hasAny(text, [
    "mueve de",
    "mueve desde",
    "mover de",
    "mover desde",
    "mueva de",
    "muebe de",
    "move from",
  ]);

const parseBoardFileToken = (token) => {
  const normalized = normalizeVoiceText(token);
  return BOARD_FILE_WORDS[normalized] || null;
};

const parseBoardRowToken = (token) => {
  const normalized = normalizeVoiceText(token);
  if (/^(?:[1-9]|1[0-3])$/.test(normalized)) {
    return Number(normalized);
  }
  return BOARD_ROW_WORDS[normalized] || null;
};

const separateAdjacentBoardCells = (text) =>
  normalizeVoiceText(text).replace(
    /\b([a-my])\s*(1[0-3]|[1-9])(?=([a-my])\s*(?:1[0-3]|[1-9])\b)/g,
    "$1$2 "
  );

const buildFocusCellCommand = (cellId) => ({
  id: `focus_cell:${cellId.toLowerCase()}`,
  type: "camera",
  label: `Enfocar ${cellId.toUpperCase()}`,
  keyData: null,
  target: cellId.toLowerCase(),
});

const extractBoardCells = (text) => {
  const searchableText = separateAdjacentBoardCells(text);
  const cells = [];
  const seenRanges = [];
  const compactPattern = /\b([a-my])\s*(1[0-3]|[1-9])\b/g;
  let match = compactPattern.exec(searchableText);
  while (match) {
    const file = match[1] === "y" ? "i" : match[1];
    cells.push(`${file}${match[2]}`.toLowerCase());
    seenRanges.push([match.index, match.index + match[0].length]);
    match = compactPattern.exec(searchableText);
  }

  const isInsideCompactMatch = (startIndex) =>
    seenRanges.some(([start, end]) => startIndex >= start && startIndex < end);
  const tokens = searchableText.split(" ").filter(Boolean);
  let searchIndex = 0;
  for (let index = 0; index < tokens.length - 1; index += 1) {
    const tokenStart = searchableText.indexOf(tokens[index], searchIndex);
    searchIndex = tokenStart >= 0 ? tokenStart + tokens[index].length : searchIndex;
    if (tokenStart >= 0 && isInsideCompactMatch(tokenStart)) continue;

    const file = parseBoardFileToken(tokens[index]);
    const row = parseBoardRowToken(tokens[index + 1]);
    if (file && row) {
      cells.push(`${file}${row}`.toLowerCase());
    }
  }

  return cells;
};

const buildBoardMoveCommand = (id, label, target, source = "") => ({
  id,
  type: "board_move",
  label,
  keyData: null,
  target,
  source,
});

const parseFocusCellCommand = (text) => {
  if (!hasFocusCellIntent(text)) return null;

  const [cellId] = extractBoardCells(text);
  if (cellId) return buildFocusCellCommand(cellId);
  return null;
};

const parseBoardMoveCommand = (text) => {
  if (hasFocusCellIntent(text)) return null;

  const cells = extractBoardCells(text);
  if (cells.length <= 0) {
    if (hasIncorporateIntent(text)) {
      if (hasSpecialBlockIntent(text)) {
        return buildBoardMoveCommand("incorporate_special", "Incorporar especial", "");
      }

      return buildBoardMoveCommand("incorporate_common", "Incorporar", "");
    }

    if (hasSpecialBlockIntent(text)) {
      return buildBoardMoveCommand("special_pending", "Especial", "");
    }
    return null;
  }

  if (hasMoveOriginIntent(text) && cells.length === 1) {
    return buildBoardMoveCommand(
      `move_origin:${cells[0]}`,
      `Origen ${cells[0].toUpperCase()}`,
      "",
      cells[0]
    );
  }

  if (hasIncorporateIntent(text)) {
    const targetCell = cells[cells.length - 1];
    if (hasSpecialBlockIntent(text)) {
      return buildBoardMoveCommand(
        `incorporate_special_cell:${targetCell}`,
        `Incorporar especial ${targetCell.toUpperCase()}`,
        targetCell
      );
    }

    return buildBoardMoveCommand(
      `incorporate_cell:${targetCell}`,
      `Incorporar ${targetCell.toUpperCase()}`,
      targetCell
    );
  }

  if (hasSpecialBlockIntent(text)) {
    if (cells.length >= 2) {
      return buildBoardMoveCommand(
        `special_cell:${cells[0]}:${cells[1]}`,
        `Especial ${cells[0].toUpperCase()} ${cells[1].toUpperCase()}`,
        cells[1],
        cells[0]
      );
    }

    return buildBoardMoveCommand(
      `special_cell:${cells[0]}`,
      `Especial ${cells[0].toUpperCase()}`,
      cells[0]
    );
  }

  if (cells.length >= 2) {
    const sourceCell = cells[0];
    const targetCell = cells[cells.length - 1];
    return buildBoardMoveCommand(
      `move_cell:${sourceCell}:${targetCell}`,
      `${sourceCell.toUpperCase()} ${targetCell.toUpperCase()}`,
      targetCell,
      sourceCell
    );
  }

  if (!hasMoveIntent(text)) {
    return buildBoardMoveCommand(
      `board_hint_cell:${cells[0]}`,
      `Casilla ${cells[0].toUpperCase()}`,
      cells[0]
    );
  }

  return buildBoardMoveCommand(
    `move_cell:${cells[0]}`,
    cells[0].toUpperCase(),
    cells[0]
  );
};

const hasAny = (normalizedText, phrases) => {
  const padded = ` ${normalizedText} `;
  return phrases.some((phrase) => padded.includes(` ${normalizeVoiceText(phrase)} `));
};

const hasExplicitRepeatIntent = (normalizedText) =>
  hasAny(normalizedText, [
    "otra vez",
    "de nuevo",
    "repite",
    "repetir",
    "repetir comando",
    "dos veces",
    "segunda vez",
  ]);

const shouldGuardRepeatedCommand = (command) =>
  ["camera", "rotation", "board_move", "dice"].includes(String(command?.type || ""));

export const normalizeVoiceText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const getVoiceRecognitionLanguage = (language) =>
  LANGUAGE_TO_RECOGNITION_LOCALE[normalizeLanguage(language)] || LANGUAGE_TO_RECOGNITION_LOCALE.es;

export const getVoiceControlStatusText = (status, language = "es") => {
  const entry = STATUS_COPY[status] || STATUS_COPY[VOICE_CONTROL_STATUS.ERROR];
  const normalizedLanguage = normalizeLanguage(language);
  return entry[normalizedLanguage] || entry.es;
};

export const getVoiceControlStatusTone = (status) => {
  if (
    status === VOICE_CONTROL_STATUS.PERMISSION_DENIED ||
    status === VOICE_CONTROL_STATUS.UNAVAILABLE ||
    status === VOICE_CONTROL_STATUS.UNSUPPORTED ||
    status === VOICE_CONTROL_STATUS.ERROR
  ) {
    return "danger";
  }
  if (
    status === VOICE_CONTROL_STATUS.AWAITING_PERMISSION ||
    status === VOICE_CONTROL_STATUS.REQUESTING_PERMISSION ||
    status === VOICE_CONTROL_STATUS.PROCESSING
  ) {
    return "pending";
  }
  if (status === VOICE_CONTROL_STATUS.DISABLED) return "disabled";
  return "active";
};

export const getVoiceRecognitionConstructor = (windowRef = typeof window !== "undefined" ? window : null) =>
  windowRef?.SpeechRecognition || windowRef?.webkitSpeechRecognition || null;

export const isVoiceRecognitionSupported = (windowRef = typeof window !== "undefined" ? window : null) =>
  Boolean(getVoiceRecognitionConstructor(windowRef));

export const parseVoiceCommand = (transcript) => {
  const normalizedTranscript = normalizeVoiceText(transcript);
  if (!normalizedTranscript) return null;
  const focusCommand = parseFocusCellCommand(normalizedTranscript);
  if (focusCommand) {
    return {
      ...focusCommand,
      transcript: normalizedTranscript,
    };
  }
  if (hasFocusCellIntent(normalizedTranscript)) return null;
  const boardMoveCommand = parseBoardMoveCommand(normalizedTranscript);
  if (boardMoveCommand) {
    return {
      ...boardMoveCommand,
      transcript: normalizedTranscript,
    };
  }
  const dieChoiceCommand = parseDieChoiceCommand(normalizedTranscript);
  if (dieChoiceCommand) {
    return {
      ...dieChoiceCommand,
      transcript: normalizedTranscript,
    };
  }
  const command = COMMANDS.find((entry) => entry.match(normalizedTranscript));
  if (!command) return null;
  return {
    id: command.id,
    type: command.type,
    label: command.label,
    keyData: command.keyData,
    transcript: normalizedTranscript,
  };
};

export const isTextEntryElement = (element) => {
  const tagName = String(element?.tagName || "").toLowerCase();
  if (["input", "textarea", "select"].includes(tagName)) return true;
  if (element?.isContentEditable) return true;
  const role = String(element?.getAttribute?.("role") || "").toLowerCase();
  return role === "textbox" || role === "combobox";
};

export const getMicrophonePermissionState = async (
  windowRef = typeof window !== "undefined" ? window : null
) => {
  try {
    const permissions = windowRef?.navigator?.permissions;
    if (!permissions || typeof permissions.query !== "function") return "unknown";
    const result = await permissions.query({ name: "microphone" });
    return String(result?.state || "unknown").toLowerCase();
  } catch {
    return "unknown";
  }
};

const getErrorStatus = (error) => {
  const errorName = String(error?.name || "").toLowerCase();
  if (
    errorName.includes("notallowed") ||
    errorName.includes("permission") ||
    errorName.includes("security")
  ) {
    return VOICE_CONTROL_STATUS.PERMISSION_DENIED;
  }
  if (errorName.includes("notfound") || errorName.includes("devicesnotfound")) {
    return VOICE_CONTROL_STATUS.UNAVAILABLE;
  }
  if (errorName.includes("notreadable") || errorName.includes("abort")) {
    return VOICE_CONTROL_STATUS.UNAVAILABLE;
  }
  return VOICE_CONTROL_STATUS.ERROR;
};

const isMobileSpeechRecognitionRuntime = (windowRef) => {
  const userAgent = String(windowRef?.navigator?.userAgent || "");
  const platform = String(windowRef?.navigator?.platform || "");
  return /android|iphone|ipad|ipod|mobile/i.test(`${userAgent} ${platform}`);
};

const applyLegacyKeyboardCodes = (event, keyData) => {
  ["keyCode", "which"].forEach((propertyName) => {
    try {
      Object.defineProperty(event, propertyName, {
        get: () => keyData.keyCode,
      });
    } catch {
      // Some browsers expose these properties as read-only. The modern key/code
      // fields above are enough for the supported path.
    }
  });
};

const createKeyboardEvent = (windowRef, type, keyData) => {
  const event = new windowRef.KeyboardEvent(type, {
    key: keyData.key,
    code: keyData.code,
    bubbles: true,
    cancelable: true,
    ctrlKey: Boolean(keyData.ctrlKey),
    shiftKey: Boolean(keyData.shiftKey),
    altKey: Boolean(keyData.altKey),
    metaKey: Boolean(keyData.metaKey),
  });
  applyLegacyKeyboardCodes(event, keyData);
  return event;
};

const dispatchKeyboardPair = (windowRef, target, keyData) => {
  if (!target || typeof target.dispatchEvent !== "function") return false;
  target.dispatchEvent(createKeyboardEvent(windowRef, "keydown", keyData));
  target.dispatchEvent(createKeyboardEvent(windowRef, "keyup", keyData));
  return true;
};

export const dispatchVoiceCommandToUnity = ({
  iframeElement,
  command,
  transcript = "",
  windowRef = typeof window !== "undefined" ? window : null,
} = {}) => {
  if (!windowRef || !command?.id) return false;

  const iframeWindow = iframeElement?.contentWindow || null;
  const iframeDocument = iframeWindow?.document || null;
  const canvas = iframeDocument?.getElementById?.("unity-canvas") || null;
  const targetOrigin = windowRef.location?.origin || "*";
  let delivered = false;

  try {
    if (iframeWindow && typeof iframeWindow.postMessage === "function") {
      iframeWindow.postMessage(
        {
          type: "XIMA_VOICE_COMMAND",
          commandId: command.id,
          commandType: command.type,
          label: command.label,
          transcript,
          target: command.target || "",
          key: command.keyData?.key || "",
          code: command.keyData?.code || "",
        },
        targetOrigin
      );
      delivered = true;
    }
  } catch {
    // Fall back to keyboard events below.
  }

  if (command.keyData) {
    const eventWindow = iframeWindow || windowRef;
    const targets = [
      canvas,
      iframeDocument?.activeElement,
      iframeDocument?.body,
      iframeDocument,
      iframeWindow,
      iframeElement,
    ].filter(Boolean);

    targets.forEach((target) => {
      try {
        delivered = dispatchKeyboardPair(eventWindow, target, command.keyData) || delivered;
      } catch {
        // Keep trying the other event targets.
      }
    });
  }

  try {
    windowRef.dispatchEvent(
      new windowRef.CustomEvent("XIMA_VOICE_COMMAND", {
        detail: {
          command,
          transcript,
        },
      })
    );
    delivered = true;
  } catch {
    // CustomEvent is optional in older embedded browsers.
  }

  return delivered;
};

export class VoiceControlController {
  constructor(options = {}) {
    this.windowRef = options.windowRef || (typeof window !== "undefined" ? window : null);
    this.onStatusChange =
      typeof options.onStatusChange === "function" ? options.onStatusChange : () => {};
    this.onCommand = typeof options.onCommand === "function" ? options.onCommand : () => {};
    this.onTranscript =
      typeof options.onTranscript === "function" ? options.onTranscript : () => {};
    this.onEngineEvent =
      typeof options.onEngineEvent === "function" ? options.onEngineEvent : () => {};
    this.shouldProcessCommand =
      typeof options.shouldProcessCommand === "function" ? options.shouldProcessCommand : () => true;
    this.language = options.language || "es";
    this.status = VOICE_CONTROL_STATUS.DISABLED;
    this.enabled = false;
    this.commandAllowed = true;
    this.recognition = null;
    this.restartTimer = null;
    this.speechResultTimer = null;
    this.lastTranscript = "";
    this.lastTranscriptAt = 0;
    this.lastCommandId = "";
    this.lastCommandAt = 0;
    this.pendingInterimTranscript = "";
    this.lastResultAt = 0;
    this.speechStartedAt = 0;
    this.microphoneLease = null;
  }

  updateOptions(options = {}) {
    if (typeof options.onStatusChange === "function") this.onStatusChange = options.onStatusChange;
    if (typeof options.onCommand === "function") this.onCommand = options.onCommand;
    if (typeof options.onTranscript === "function") this.onTranscript = options.onTranscript;
    if (typeof options.onEngineEvent === "function") this.onEngineEvent = options.onEngineEvent;
    if (typeof options.shouldProcessCommand === "function") {
      this.shouldProcessCommand = options.shouldProcessCommand;
    }
    if (options.language) this.language = options.language;
  }

  setStatus(status) {
    this.status = status;
    this.onStatusChange(status);
  }

  setCommandAllowed(allowed) {
    this.commandAllowed = allowed !== false;
    if (this.enabled && !this.commandAllowed && this.status === VOICE_CONTROL_STATUS.LISTENING) {
      this.setStatus(VOICE_CONTROL_STATUS.ACTIVE);
    }
  }

  async start({ requestPermission = false } = {}) {
    this.enabled = true;
    this.clearRestartTimer();
    this.clearSpeechResultTimer();

    if (!this.windowRef) {
      this.setStatus(VOICE_CONTROL_STATUS.UNSUPPORTED);
      this.enabled = false;
      return false;
    }

    const Recognition = getVoiceRecognitionConstructor(this.windowRef);
    if (!Recognition) {
      this.setStatus(VOICE_CONTROL_STATUS.UNSUPPORTED);
      this.enabled = false;
      return false;
    }

    const mediaDevices = this.windowRef.navigator?.mediaDevices;
    if (!mediaDevices) {
      this.setStatus(VOICE_CONTROL_STATUS.UNAVAILABLE);
      this.enabled = false;
      return false;
    }

    const permissionState = await getMicrophonePermissionState(this.windowRef);
    if (permissionState === "denied") {
      this.setStatus(VOICE_CONTROL_STATUS.PERMISSION_DENIED);
      this.enabled = false;
      return false;
    }
    if (!requestPermission && permissionState !== "granted") {
      this.setStatus(VOICE_CONTROL_STATUS.AWAITING_PERMISSION);
      return false;
    }

    this.setStatus(VOICE_CONTROL_STATUS.REQUESTING_PERMISSION);
    this.cleanupRecognition();

    try {
      if (!this.enabled) {
        this.setStatus(VOICE_CONTROL_STATUS.DISABLED);
        return false;
      }
      if (!this.microphoneLease) {
        this.microphoneLease = await acquirePersistentMicrophone({
          windowRef: this.windowRef,
        });
      }
      if (!this.enabled) {
        this.releaseMicrophone();
        this.setStatus(VOICE_CONTROL_STATUS.DISABLED);
        return false;
      }
      this.onEngineEvent({ stage: "permission_granted" });
      this.createRecognition(Recognition);
      return this.startRecognition();
    } catch (error) {
      this.enabled = false;
      this.releaseMicrophone();
      this.cleanupRecognition();
      const nextStatus = getErrorStatus(error);
      this.setStatus(nextStatus);
      return false;
    }
  }

  createRecognition(Recognition) {
    this.cleanupRecognition();
    const recognition = new Recognition();
    recognition.lang = getVoiceRecognitionLanguage(this.language);
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      if (!this.enabled) return;
      this.onEngineEvent({ stage: "recognition_started" });
      this.setStatus(this.commandAllowed ? VOICE_CONTROL_STATUS.LISTENING : VOICE_CONTROL_STATUS.ACTIVE);
    };
    recognition.onaudiostart = recognition.onspeechstart = () => {
      if (!this.enabled) return;
      this.speechStartedAt = Date.now();
      this.onEngineEvent({ stage: "speech_started" });
      this.scheduleSpeechResultFallback(2800);
      this.setStatus(this.commandAllowed ? VOICE_CONTROL_STATUS.LISTENING : VOICE_CONTROL_STATUS.ACTIVE);
    };
    recognition.onspeechend = () => {
      if (!this.enabled) return;
      this.onEngineEvent({ stage: "speech_ended" });
      this.scheduleSpeechResultFallback(650);
      this.setStatus(VOICE_CONTROL_STATUS.ACTIVE);
    };
    recognition.onresult = (event) => this.handleResult(event);
    recognition.onerror = (event) => this.handleRecognitionError(event);
    recognition.onend = () => this.handleRecognitionEnd();

    this.recognition = recognition;
  }

  startRecognition() {
    if (!this.enabled || !this.recognition) return false;
    try {
      this.recognition.start();
      this.onEngineEvent({ stage: "start_requested" });
      return true;
    } catch (error) {
      this.onEngineEvent({
        stage: "start_failed",
        error: String(error?.message || error?.name || error || ""),
      });
      this.scheduleRestart();
      return false;
    }
  }

  handleResult(event) {
    if (!this.enabled) return;

    const resultIndex = Math.max(0, Number(event?.resultIndex) || 0);
    const results = event?.results || [];
    for (let index = resultIndex; index < results.length; index += 1) {
      const result = results[index];
      if (!result) continue;

      const transcript = String(result?.[0]?.transcript || "").trim();
      const normalizedTranscript = normalizeVoiceText(transcript);
      if (!normalizedTranscript) continue;

      const isFinal = result.isFinal !== false;
      this.lastResultAt = Date.now();
      this.pendingInterimTranscript = isFinal ? "" : normalizedTranscript;
      if (isFinal) this.clearSpeechResultTimer();
      this.onEngineEvent({
        stage: "result_received",
        transcript,
        normalizedTranscript,
        isFinal,
      });

      const command = parseVoiceCommand(normalizedTranscript);
      if (!command) {
        this.onTranscript({
          transcript,
          normalizedTranscript,
          stage: isFinal ? "unmatched" : "interim_unmatched",
        });
        continue;
      }

      if (this.isDuplicateCommand(command, normalizedTranscript)) {
        continue;
      }

      if (!this.shouldProcessCommand(command)) {
        this.onTranscript({
          transcript,
          normalizedTranscript,
          command,
          stage: "blocked",
        });
        this.setStatus(VOICE_CONTROL_STATUS.ACTIVE);
        continue;
      }

      this.onTranscript({
        transcript,
        normalizedTranscript,
        command,
        stage: isFinal ? "matched" : "matched_interim",
      });
      this.setStatus(VOICE_CONTROL_STATUS.PROCESSING);
      this.onCommand(command, normalizedTranscript);
      this.lastTranscript = normalizedTranscript;
      this.lastTranscriptAt = Date.now();
      this.lastCommandId = command.id;
      this.lastCommandAt = this.lastTranscriptAt;
      this.pendingInterimTranscript = "";
      this.clearSpeechResultTimer();
      this.setStatus(VOICE_CONTROL_STATUS.LISTENING);
    }
  }

  isDuplicateCommand(command, normalizedTranscript) {
    const now = Date.now();
    const isDuplicateTranscript =
      normalizedTranscript === this.lastTranscript &&
      now - this.lastTranscriptAt < DUPLICATE_TRANSCRIPT_WINDOW_MS;
    const isDuplicateCommand =
      command?.id &&
      command.id === this.lastCommandId &&
      now - this.lastCommandAt < DUPLICATE_TRANSCRIPT_WINDOW_MS;
    const isGuardedRepeat =
      shouldGuardRepeatedCommand(command) &&
      command?.id &&
      command.id === this.lastCommandId &&
      now - this.lastCommandAt < REPEAT_COMMAND_GUARD_WINDOW_MS &&
      !hasExplicitRepeatIntent(normalizedTranscript);
    return Boolean(isDuplicateTranscript || isDuplicateCommand || isGuardedRepeat);
  }

  handleRecognitionError(event) {
    if (!this.enabled) return;
    const errorName = String(event?.error || event?.name || "").toLowerCase();
    if (errorName.includes("not-allowed") || errorName.includes("permission")) {
      this.onEngineEvent({ stage: "permission_denied", error: errorName });
      this.enabled = false;
      this.clearSpeechResultTimer();
      this.setStatus(VOICE_CONTROL_STATUS.PERMISSION_DENIED);
      return;
    }
    if (errorName.includes("no-speech") || errorName.includes("aborted")) {
      this.onEngineEvent({ stage: errorName.includes("no-speech") ? "no_speech" : "aborted" });
      this.setStatus(VOICE_CONTROL_STATUS.ACTIVE);
      return;
    }
    this.onEngineEvent({ stage: "recognition_error", error: errorName });
    this.setStatus(VOICE_CONTROL_STATUS.ERROR);
  }

  handleRecognitionEnd() {
    if (!this.enabled) return;
    this.clearSpeechResultTimer();
    if (this.speechStartedAt > this.lastResultAt) {
      this.onEngineEvent({
        stage: "ended_without_result",
        transcript: this.pendingInterimTranscript,
      });
    }
    this.onEngineEvent({ stage: "recognition_ended" });
    this.scheduleRestart();
  }

  scheduleRestart() {
    if (!this.enabled || !this.windowRef?.setTimeout) return;
    this.clearRestartTimer();
    this.restartTimer = this.windowRef.setTimeout(() => {
      this.restartTimer = null;
      this.startRecognition();
    }, 350);
  }

  scheduleSpeechResultFallback(delayMs) {
    if (!this.enabled || !this.recognition || !this.windowRef?.setTimeout) return;
    this.clearSpeechResultTimer();
    const startedAt = this.speechStartedAt;
    const mobileDelayMs = isMobileSpeechRecognitionRuntime(this.windowRef)
      ? Math.max(delayMs, 4200)
      : delayMs;
    this.speechResultTimer = this.windowRef.setTimeout(() => {
      this.speechResultTimer = null;
      if (!this.enabled || !this.recognition) return;
      if (this.lastResultAt >= startedAt) return;
      this.onEngineEvent({ stage: "forcing_result", delayMs });
      try {
        this.recognition.stop?.();
      } catch {
        try {
          this.recognition.abort?.();
        } catch {
          // If the browser refuses to end recognition, the next restart will recover.
        }
      }
    }, mobileDelayMs);
  }

  clearRestartTimer() {
    if (this.restartTimer && this.windowRef?.clearTimeout) {
      this.windowRef.clearTimeout(this.restartTimer);
    }
    this.restartTimer = null;
  }

  clearSpeechResultTimer() {
    if (this.speechResultTimer && this.windowRef?.clearTimeout) {
      this.windowRef.clearTimeout(this.speechResultTimer);
    }
    this.speechResultTimer = null;
  }

  cleanupRecognition() {
    this.clearSpeechResultTimer();
    const recognition = this.recognition;
    this.recognition = null;
    if (!recognition) return;
    recognition.onstart = null;
    recognition.onaudiostart = null;
    recognition.onspeechstart = null;
    recognition.onspeechend = null;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.abort?.();
    } catch {
      try {
        recognition.stop?.();
      } catch {
        // Nothing else to release.
      }
    }
  }

  releaseMicrophone() {
    const lease = this.microphoneLease;
    this.microphoneLease = null;
    try {
      lease?.release?.();
    } catch {
      // Shared microphone cleanup is best-effort.
    }
  }

  stop() {
    this.enabled = false;
    this.clearRestartTimer();
    this.clearSpeechResultTimer();
    this.cleanupRecognition();
    this.releaseMicrophone();
    this.lastTranscript = "";
    this.lastTranscriptAt = 0;
    this.lastCommandId = "";
    this.lastCommandAt = 0;
    this.pendingInterimTranscript = "";
    this.lastResultAt = 0;
    this.speechStartedAt = 0;
    this.setStatus(VOICE_CONTROL_STATUS.DISABLED);
  }

  destroy() {
    this.stop();
    this.onCommand = () => {};
    this.onStatusChange = () => {};
    this.onTranscript = () => {};
    this.onEngineEvent = () => {};
    this.shouldProcessCommand = () => false;
  }
}

export const createVoiceControlController = (options) => new VoiceControlController(options);
